import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Hand, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

type RequestedItem = {
  id: string;
  name: string;
  status: string;
  imageUrl: string | null;
  price: string;
  requestCount: number;
};

function statusLabel(status: string): string {
  if (status === "published_pending_payments") return "Waiting on your payout setup";
  if (status === "archived") return "You took this down";
  if (status === "on_request") return "Available on request";
  return status;
}

// Turns the marketplace demand-side up. A maker with intermittent supply (offal,
// surplus, a cut they only sometimes have) can leave an item as on_request and
// find out how many people want it before doing the work.
export function SellerRequestQueue() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: requests = [], isLoading } = useQuery<RequestedItem[]>({
    queryKey: ["/api/seller/requests"],
    queryFn: async () => {
      const res = await fetch("/api/seller/requests", { credentials: "include" });
      if (!res.ok) throw new Error("Couldn't load requests");
      return res.json();
    },
  });

  const makeAvailable = useMutation({
    mutationFn: async (itemId: string) => {
      const res = await fetch(`/api/items/${itemId}/make-available`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || "Couldn't publish that");
      }
      return res.json() as Promise<{ notifiedWatchers: boolean }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/seller/requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
      toast({
        title: "It's live",
        description: data.notifiedWatchers
          ? "Everyone who asked for it has been told."
          : "Finish your payout setup and buyers can order it.",
      });
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't publish", description: err.message, variant: "destructive" });
    },
  });

  if (isLoading) return null;

  // Only worth showing once somebody has actually asked for something. An empty
  // queue on a new seller's dashboard is noise.
  const withDemand = requests.filter((r) => r.requestCount > 0);
  if (withDemand.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-brand-secondary/40 bg-brand-secondary/5 p-3">
      <div className="flex items-center gap-2">
        <Hand className="h-4 w-4 text-brand-secondary" />
        <p className="text-sm font-semibold text-foreground">People are asking for these</p>
      </div>
      <div className="mt-3 space-y-2">
        {withDemand.map((item) => (
          <div
            key={item.id}
            className="flex items-center gap-3 rounded-md border bg-background p-2"
          >
            {item.imageUrl ? (
              <img
                src={item.imageUrl}
                alt=""
                className="h-10 w-10 shrink-0 rounded object-cover"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{item.name}</p>
              <p className="text-xs text-muted-foreground">
                {item.requestCount} {item.requestCount === 1 ? "person" : "people"} waiting ·{" "}
                {statusLabel(item.status)}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => makeAvailable.mutate(item.id)}
              disabled={makeAvailable.isPending}
            >
              {makeAvailable.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                "I have it"
              )}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
